import type { Moment } from "moment"
import type { WorkspaceRecurrence } from "@/workspace/interfaces/api/workspace-recurrence.interface"
import { BaseModelWithFiles } from "@/models/base-with-files.model"
import { ModelFactory } from "@/factories/model-factory"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model"
import type { WorkspaceAudienceGroup } from "@/workspace/models/workspace-audience-group.model"
import { WorkspaceNoteAudienceGroup } from "@/workspace/models/workspace-note-audience-group.model"
import { WorkspaceNoteRecipient } from "@/workspace/models/workspace-note-recipient.model"
import { WorkspaceChecklistItem } from "@/workspace/models/workspace-checklist-item.model"
import type {
  IWorkspaceNote,
  WorkspaceNoteStatus
} from "@/workspace/interfaces/api/workspace-note.interface"
import type {
  IWorkspaceAttachment,
  WorkspaceAudienceKind
} from "@/workspace/interfaces/component/workspace.interface"

export class WorkspaceNote extends BaseModelWithFiles implements IWorkspaceNote {
  public static className = "WorkspaceNote" // Used for socket communication.
  public static verboseName = "werkruimtenotitie"

  public static override managedResolveFields = [
    "author",
    "assignee",
    "recipients",
    "audienceGroups"
  ]

  declare id: string
  recurrence: WorkspaceRecurrence | null
  seriesId: string | null
  seriesDeleted: boolean
  seriesStopped: boolean
  recurrenceStart: Moment | null
  occurrenceAt: Moment | null
  frozen: boolean
  author: KnownBusinessEntityContact
  assignee: KnownBusinessEntityContact | null
  body: string
  /** Direct sharing records. */
  recipients: WorkspaceNoteRecipient[]
  /** Group sharing records; membership is evaluated through each linked group. */
  audienceGroups: WorkspaceNoteAudienceGroup[]
  checklistItems: WorkspaceChecklistItem[]
  remindAt: Moment | null
  reminderSetAt: Moment | null
  reminderDateOnly: boolean
  status: WorkspaceNoteStatus
  completedAt: Moment | null
  deletedAt: Moment | null

  constructor(note: IWorkspaceNote, managed = false) {
    super(note, managed)
    this.recurrence = note.recurrence ?? null
    this.seriesId = note.seriesId ?? null
    this.seriesDeleted = Boolean(note.seriesDeleted)
    this.seriesStopped = Boolean(note.seriesStopped)
    this.recurrenceStart = note.recurrenceStart ?? null
    this.occurrenceAt = note.occurrenceAt ?? null
    this.frozen = Boolean(note.frozen)

    this.author = ModelFactory.childModel(KnownBusinessEntityContact, note.author, managed)
    this.assignee = note.assignee
      ? ModelFactory.childModel(KnownBusinessEntityContact, note.assignee, managed)
      : null
    this.body = note.body
    this.recipients = ModelFactory.childModels(WorkspaceNoteRecipient, note.recipients, managed)
    this.audienceGroups = ModelFactory.childModels(
      WorkspaceNoteAudienceGroup,
      note.audienceGroups,
      managed
    )
    this.checklistItems = ModelFactory.childModels(
      WorkspaceChecklistItem,
      note.checklistItems ?? [],
      managed
    )
    this.remindAt = note.remindAt ?? null
    this.reminderSetAt = note.reminderSetAt ?? null
    this.reminderDateOnly = Boolean(this.remindAt && note.reminderDateOnly)
    this.status = note.status
    this.completedAt = note.completedAt ?? null
    this.deletedAt = note.deletedAt ?? null
  }

  /* Id views of the nested models, as the cards and pickers read them. Getters are not serialized. */

  public get authorId(): string {
    return this.author?.id ?? ""
  }

  public get assigneeId(): string | null {
    return this.assignee?.id ?? null
  }

  public get recipientIds(): string[] {
    return this.recipientContacts.map((contact) => contact.id)
  }

  public get recipientContacts(): KnownBusinessEntityContact[] {
    return this.recipients.map((recipient) => recipient.knownBusinessEntityContact)
  }

  public get sharedAudienceGroups(): WorkspaceAudienceGroup[] {
    return this.audienceGroups.map((link) => link.audienceGroup)
  }

  public get audienceGroupIds(): string[] {
    return this.sharedAudienceGroups.map((group) => group.id)
  }

  public get audienceGroupNames(): string[] {
    return this.sharedAudienceGroups.map((group) => group.name)
  }

  /** Apply picker selections while retaining the identity of unchanged sharing records. */
  public setRecipients(contacts: KnownBusinessEntityContact[]): void {
    const existing = new Map(
      this.recipients.map((recipient) => [recipient.knownBusinessEntityContact.id, recipient])
    )
    this.recipients = contacts.map(
      (contact) =>
        existing.get(contact.id) ??
        ModelFactory.createModel(
          WorkspaceNoteRecipient,
          { note: this.id, knownBusinessEntityContact: contact },
          this.isManaged
        )
    )
  }

  public setAudienceGroups(groups: WorkspaceAudienceGroup[]): void {
    const existing = new Map(this.audienceGroups.map((link) => [link.audienceGroup.id, link]))
    this.audienceGroups = groups.map(
      (group) =>
        existing.get(group.id) ??
        ModelFactory.createModel(
          WorkspaceNoteAudienceGroup,
          { note: this.id, audienceGroup: group },
          this.isManaged
        )
    )
  }

  public get audienceKind(): WorkspaceAudienceKind {
    if (this.audienceGroups.length > 0) return "group"
    return this.recipients.length > 0 ? "custom" : "private"
  }

  /** Everyone who may see the note: author, assignees, recipients and the current members of its groups. */
  public get ownerIds(): string[] {
    const ids = [
      this.authorId,
      this.assigneeId,
      ...this.recipientIds,
      ...this.sharedAudienceGroups.flatMap((group) => group.memberIds),
      ...this.checklistItems.map((item) => item.assigneeId)
    ]
    return [...new Set(ids.filter((id): id is string => Boolean(id)))]
  }

  public get attachments(): IWorkspaceAttachment[] {
    return this.fileBucket.fileRelations.map((fileRelation) => ({
      id: fileRelation.id!,
      fileRelation
    }))
  }

  public isOwnedBy(knownBusinessEntityContactId: string): boolean {
    return this.ownerIds.includes(knownBusinessEntityContactId)
  }
}
